import { Location } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import {
  LuxonDateAdapter,
  MAT_LUXON_DATE_ADAPTER_OPTIONS,
  MAT_LUXON_DATE_FORMATS,
  provideLuxonDateAdapter,
} from '@angular/material-luxon-adapter';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, Router } from '@angular/router';
import {
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
  AccountInfo,
} from '@azure/msal-browser';
import { Providers, SimpleProvider, TemplateHelper, ProviderState } from '@microsoft/mgt';
import { provideMarkdown } from 'ngx-markdown';
import { catchError, concatMap, EMPTY, forkJoin, lastValueFrom, map, of, tap } from 'rxjs';

import { ObservabilityService, UserService } from '@core/services';

import { PermissionService, UserScopeService } from '@shared/service';

import { environment } from '@environments';

import { routes } from './app.routes';
import { ErrorHttpInterceptor } from './error-http-interceptor.service';
import { TVAHttpInterceptor } from './http-interceptor.service';

const appInitializer = () => {
  const msalService = inject(MsalService);
  const router = inject(Router);
  const location = inject(Location);
  const userService = inject(UserService);
  const permissionsService = inject(PermissionService);
  const userScopeService = inject(UserScopeService);
  const observabilityService = inject(ObservabilityService);

  return msalService.handleRedirectObservable().pipe(
    concatMap((response) => {
      const accounts = msalService.instance.getAllAccounts();

      if (!response && accounts.length === 0) {
        router.navigate(['auth']);
        return EMPTY;
      }

      const activeAccount = accounts[0];
      msalService.instance.setActiveAccount(activeAccount);

      setupMicrosoftGraphProvider(msalService, activeAccount);
      TemplateHelper.setBindingSyntax('[[', ']]');
      Providers.globalProvider.setState(ProviderState.SignedIn);

      return loadUserRolesAndPermissions(userService).pipe(
        tap(({ roles, permissions, capabilities }) => {
          if (location.path() === '/auth') {
            router.navigate(['home']);
          }

          const currentUser = {
            id: activeAccount.idTokenClaims?.oid as string,
            email: activeAccount.idTokenClaims?.preferred_username as string,
            first_name: '',
            last_name: '',
          };
          permissionsService.setUserRolesAndPermissions(
            currentUser,
            roles,
            permissions,
            capabilities.available_user_scopes,
          );
          userScopeService.initialize(currentUser.id);
          observabilityService.setUserContext(
            activeAccount.idTokenClaims?.oid as string,
            activeAccount.idTokenClaims?.preferred_username as string,
          );
        }),
        catchError((error) => {
          if ([401, 403].includes(error?.status)) {
            router.navigate(['unauthorized']);
            return EMPTY;
          }

          throw error;
        }),
      );
    }),
  );
};

// Setup Microsoft Graph Toolkit provider
function setupMicrosoftGraphProvider(msalService: MsalService, account: AccountInfo) {
  Providers.globalProvider = new SimpleProvider(
    async (scopes: string[]) => {
      return await lastValueFrom(
        msalService.acquireTokenSilent({ scopes, account }).pipe(map((token) => token.accessToken)),
      );
    },
    () => Promise.resolve(),
    () => lastValueFrom(msalService.logout()),
  );
}

// Load user roles and permissions
function loadUserRolesAndPermissions(userService: UserService) {
  return forkJoin({
    roles: userService.GetRoles(),
    permissions: userService.GetPermissions(),
    capabilities: userService
      .GetCapabilities()
      .pipe(catchError(() => of({ available_user_scopes: [] }))),
  });
}

export function loggerCallback(logLevel: LogLevel, message: string) {
  if (environment.dev) {
    switch (logLevel) {
      case LogLevel.Error:
        console.error(message);
        break;
      case LogLevel.Warning:
        console.warn(message);
        break;
      case LogLevel.Trace:
        console.trace(message);
        break;
      default:
        console.log(message);
    }
  } else if (environment.beta && logLevel === LogLevel.Error) {
    console.error(message);
  }
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: '/home',
      postLogoutRedirectUri: '/auth',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();

  protectedResourceMap.set(environment.apiConfig.uri, environment.apiConfig.scopes);
  protectedResourceMap.set(environment.API_URL, environment.apiConfig.scopes);
  protectedResourceMap.set(environment.graphConfig.photoUri, environment.graphConfig.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/auth',
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(
      withInterceptors([TVAHttpInterceptor, ErrorHttpInterceptor]),
      withInterceptorsFromDi(),
    ),

    // Luxon date adapter configs
    provideLuxonDateAdapter(),
    {
      provide: MAT_LUXON_DATE_ADAPTER_OPTIONS,
      useValue: { firstDayOfWeek: 7 },
    },
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_LUXON_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-IN' },

    // MSAL
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,

    // App Initializer
    provideAppInitializer(appInitializer),

    // ngx-markdown
    provideMarkdown(),
  ],
};
